import "swiper/css";
import "swiper/css/navigation";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useCurrentPageType } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { useEditionValue } from "shared/lib/edition";

import type { headerOnelineMenuQuery } from "scmp-app/queries/__generated__/headerOnelineMenuQuery.graphql";

import {
  Container,
  MenuItem,
  StyledBaseEntityLink,
  StyledSwiper,
  StyledSwiperSlide,
} from "./styles";

type Props = { initEdition?: Edition };
export const HeaderOnelineMenu: FunctionComponent<Props> = ({ initEdition }) => {
  const edition = useEditionValue({ initializeWithValue: true });
  const finalEdition = initEdition ?? edition;
  const queryReference = useLazyLoadQuery<headerOnelineMenuQuery>(
    graphql`
      query headerOnelineMenuQuery {
        pwaOnelineMenu: appConfig(filter: { entityId: "scmp_pwa_oneline_menu" }) {
          json
        }
      }
    `,
    {},
  );

  const onelineMenuAppConfig = queryReference?.pwaOnelineMenu?.json as
    | ScmpPwaOnelineMenuAppConfig
    | undefined;
  const menuItems = onelineMenuAppConfig?.list?.[finalEdition];

  const currentPageType = useCurrentPageType();

  const content = useMemo(
    () => (
      <Container>
        <StyledSwiper cssMode={false} navigation={false} pagination={false} slidesPerView="auto">
          {menuItems?.map((item, index) => (
            <StyledSwiperSlide key={index}>
              <StyledBaseEntityLink
                linkEntity={item}
                query={{
                  module: `oneline_menu_section_${finalEdition}`,
                  pgtype: currentPageType,
                }}
              >
                <MenuItem>{item.name}</MenuItem>
              </StyledBaseEntityLink>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </Container>
    ),
    [menuItems, finalEdition, currentPageType],
  );
  if (!menuItems) return null;

  if (initEdition === undefined) {
    return <ClientSideSuspense>{content}</ClientSideSuspense>;
  }

  return content;
};

HeaderOnelineMenu.displayName = "HeaderOnelineMenu";
